import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSingleEffect } from 'react-haiku';
import { useCollect, useRequest } from 'hooks';

import DayJs from 'utils/Dayjs';
import { GRADE_STUDENT_STATES } from 'utils/Constants';

import Modal from 'components/Modal';
import Icon from 'components/Misc/Icon';
import Progress from 'components/Misc/Progress';
import { Button, Datepicker, Select } from 'components/Misc/Form';

import { useStudentProfileStore } from 'store/user/useStudentProfileStore';

const Edit = ({ id, onModal }) => {
	// Cache current object's id
	const params = useParams();

	// Use student's profile store
	const [grade, onUpdateGrade] = useStudentProfileStore(state => [
		state.grades.find(g => g.id === id) || {},
		state.onUpdateGrade
	]);

	// Store update data
	const [data, setData] = useState({
		startDate: '',
		endDate: '',
		state: ''
	});

	// Store put request
	const [[updating], update] = useRequest('put', `users/${params.id}`);

	// Handle date change
	const onChange = ({ name, value }) => {
		setData(prev => ({
			...prev,
			[name]: value
		}));
	};

	// Handle data update
	const onSubmit = async (event) => {
		event.preventDefault();
		
		console.log('update:', data);

		// Do not submit if request in progress
		if (updating) return;

		// Create formdata's instance
		const formdata = new FormData();

		// Append all plain text data as stringified object
		formdata.append('data', JSON.stringify({
			...data,
			id: grade.id
		}));

		// Try to update material
		const [success, error] = await update(formdata, { related: 'student:grade' });

		// TODO: Think of a better way of handling success
		if (success) {
			// Update profile's state
			onUpdateGrade(id, data);

			// Close modal
			onModal();
		}

		// TODO: Thin of a better way of handling errors
		if (error) return console.log(error);
	};

	// Update data on id change
	useEffect(() => {
		setData(prev => ({
			...prev,
			startDate: grade.startDate,
			endDate: grade.endDate,
			state: grade.state
		}));
	}, [id]);

	return (
		<Modal title="Labot klases datus" visible={id !== 0} handleModal={onModal}>
			<div className="grid grid-cols-2 gap-4">
				<Datepicker
					asInput
					label="Sākuma datums"
					name="startDate"
					value={data.startDate}
					onChange={onChange}
				/>

				<Datepicker
					end
					asInput
					label="Beigu datums"
					name="endDate"
					value={data.endDate}
					onChange={onChange}
				/>

				<Select
					name="state"
					label="Statuss"
					value={data.state}
					onChange={onChange}
					options={GRADE_STUDENT_STATES.map(state => ({
						value: state.value, name: state.text
					}))}
				/>
			</div>

			<div className="mt-4 flex justify-end">
				<Button
					submit
					value="Saglabāt izmaiņas"
					onClick={onSubmit}
				/>
			</div>
		</Modal>
	);
};

const Grade = ({ grade, onEdit }) => {
	const state = useMemo(() => GRADE_STUDENT_STATES.find(s => s.value === grade.state), [grade]);

	return (
		<tr className="h-12">
			<td className="border-t border-neutral-100 px-4 font-semibold">{grade.title}</td>
			<td className="border-t border-neutral-100 px-4 w-full">{grade.program.title}</td>
			<td className="border-t border-neutral-100 px-4">{DayJs(grade.startDate).format('D. MMM, YYYY')}</td>
			<td className="border-t border-neutral-100 px-4">{DayJs(grade.endDate).format('D. MMM, YYYY')}</td>
			<td className="border-t border-neutral-100 px-4">
				<div className="inline-block h-7 leading-7 px-2 font-semibold text-neutral-700 rounded-lg bg-neutral-100">{state.text}</div>
			</td>
			<td className="border-t border-neutral-100 px-4">
				<Icon
					icon="more_vert"
					className="w-7 h-7 rounded-lg text-center flex items-center justify-center font-bold text-lg hover:bg-neutral-100 cursor-pointer"
					onClick={() => onEdit(grade.id)}
				/>
			</td>
		</tr>
	);
};

const Grades = ({ tab, setTab }) => {
	// Cache current object's id
	const { id } = useParams();

	// Update selected tab on mount
	useSingleEffect(() => setTab(tab));

	// Store modal's state
	const [active, setActive] = useState(0);

	// Use student's profile store
	const [grades, onInit] = useStudentProfileStore(state => [
		state.grades,
		state.onInit
	]);

	// Store grade request parameters
	const parameters = useMemo(() => ({
		related: 'students:grades',
		orderBy: 'startDate',
		eager: 'program'
	}), []);

	// Store requested grades
	const [[result, progress], collect] = useCollect(`users/${id}`, parameters);

	// Collect user on mount and on parameter change
	useEffect(() => {
		(async () => await collect())();
	}, [collect]);

	// Initialize student's grades after collection
	useEffect(() => {
		if (result) onInit('grades', result);
	}, [result]);

	// Handle grade's selection
	const onEdit = (id) => {
		setActive(id);
	};

	// Handle modal closing
	const onModal = () => {
		setActive(0);
	};

	return (
		<div className="mt-4 p-4 space-y-4 bg-white dark:bg-neutral-800 rounded-lg transition-colors">
			{/* Show loading progress */}
			<Progress progress={!grades?.length && progress} />

			{/* Show nothing found */}
			{/* <Nothing visible={!grades?.length && !progress} /> */}

			<table className="w-full whitespace-nowrap">
				<thead className="text-left text-sm text-neutral-500 dark:text-neutral-400 font-normal">
					<tr className="bg-neutral-100 dark:bg-neutral-800 h-10">
						<th className="px-4 font-normal rounded-l-lg">Klase</th>
						<th className="px-4 w-full font-normal">Programma</th>
						<th className="px-4 font-normal">Sākuma datums</th>
						<th className="px-4 font-normal">Beigu datums</th>
						<th className="px-4 font-normal">Statuss</th>
						<th className="px-4 font-normal rounded-r-lg"></th>
					</tr>
				</thead>
				<tbody className="text-sm text-neutral-700 dark:text-neutral-100">
					{grades?.map(grade =>
						<Grade key={grade.id} grade={grade} onEdit={onEdit} />
					)}
				</tbody>
			</table>

			<Edit id={active} onModal={onModal} />
		</div>
	);
};

export default Grades;